/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import Content from '@component/common/content';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import SideTab from '@component/gridgio/navigator/side-tab';
import IconBlock from '@component/common/block/icon-block';
import HeaderContainer from '@component/common/architecture/header/header-container';
import { IS_GRIDGIO_MOBILE_VIEW } from '@config/environment';
import { actions as navigationActions } from '@store/slice/navigation-slice';
import { Helmet } from 'react-helmet';
import { useAuthPage } from '@hook/auth/useAuth';
import TextBlock from '@component/common/block/text-block';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';

interface IProps {
  title: string;
  mode: 'page' | 'back' | 'close';
  children: React.ReactNode;
  handleNavigationClick?: () => void;
  customHeader?: React.ReactNode;
  backgroundColor?: string;
  hideSignin?: boolean;
}

const HEADER_ICON_WIDTH = '30px';

const HEADER_ICON_DESIGN = {
  icon: { size: 'medium' },
  box: { width: HEADER_ICON_WIDTH, left: '15px', event: { cursor: 'pointer' } },
} as const;

const HEADER_CONTAINER_DESIGN = {
  left: IS_GRIDGIO_MOBILE_VIEW ? '0px' : size.GRIDGIO_SIDE_WIDTH,
  right: '0px',
  height: IS_GRIDGIO_MOBILE_VIEW ? size.MOBILE_HEADER_HEIGHT : size.WEB_HEADER_HEIGHT,
  align: IS_GRIDGIO_MOBILE_VIEW ? 'around' : 'right',
  // backgroundColor: color.GRAY_4,
  backgroundColor: color.WHITE_1,
  boxShadow: color.HEADER_SHADOW,
  zIndex: 10,
} as const;

const HEADER_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2 },
  box: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
} as const;

const SIGNIN_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { event: { cursor: 'pointer' }, right: '15px' },
} as const;

const Island = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
  flex-direction: column;
`;

const CustomHeaderContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  width: calc(100% - 2 * 80px);
`;

const PageTemplate: React.FC<IProps> = ({
  title,
  children,
  mode,
  handleNavigationClick,
  customHeader,
  backgroundColor,
  hideSignin = false,
}) => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const isMobileSideTab = useTypedSelector((state) => state.rootReducer?.navigationReducer?.isMobileSideTab) ?? false;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;

  // ############### api ###############

  // ############### handler ###############
  const handleMenuClick = () => {
    if (handleNavigationClick) return handleNavigationClick();
    dispatch(navigationActions.openMobileSideTab());
    dispatch(modalActions.hideOverlay());
  };
  const handleSigninClick = () => openAuthPage('signin');

  // ############### etc ###############
  const { openAuthPage } = useAuthPage();

  return (
    <>
      <Helmet>
        <title>{`${title} - GridgIO`}</title>
      </Helmet>
      {IS_GRIDGIO_MOBILE_VIEW ? (
        <>
          {isMobileSideTab && <SideTab />}
          {customHeader !== 'none' && (
            <HeaderContainer design={HEADER_CONTAINER_DESIGN}>
              <IconBlock
                icon={mode === 'page' ? 'menu' : mode === 'close' ? 'close' : 'back'}
                design={HEADER_ICON_DESIGN}
                click={{ handler: handleMenuClick }}
              />
              {customHeader ? (
                <CustomHeaderContainer>{customHeader}</CustomHeaderContainer>
              ) : (
                <TextBlock text={title} design={HEADER_TEXT_DESIGN} />
              )}
              {!isAuthenticated && !hideSignin && (
                <TextBlock text={`로그인`} design={SIGNIN_TEXT_DESIGN} click={{ handler: handleSigninClick }} />
              )}
            </HeaderContainer>
          )}
          <Content
            top={customHeader === 'none' ? 'var(--sat)' : `${size.MOBILE_HEADER_HEIGHT} + var(--sat)`}
            bottom='var(--sab)'
            backgroundColor={backgroundColor}>
            <Island>{children}</Island>
          </Content>
        </>
      ) : (
        <>
          <SideTab />
          {customHeader !== 'none' && (
            <HeaderContainer design={HEADER_CONTAINER_DESIGN}>
              {mode !== 'page' && (
                <IconBlock
                  icon={mode === 'close' ? 'close' : 'back'}
                  design={HEADER_ICON_DESIGN}
                  click={{ handler: handleMenuClick }}
                />
              )}
              {customHeader ? (
                <CustomHeaderContainer>{customHeader}</CustomHeaderContainer>
              ) : (
                <TextBlock text={title} design={HEADER_TEXT_DESIGN} />
              )}
              {!isAuthenticated && !hideSignin && (
                <TextBlock text={`로그인`} design={SIGNIN_TEXT_DESIGN} click={{ handler: handleSigninClick }} />
              )}
            </HeaderContainer>
          )}
          <Content
            left={size.GRIDGIO_SIDE_WIDTH}
            top={customHeader === 'none' ? '0px' : size.WEB_HEADER_HEIGHT}
            bottom='0px'
            backgroundColor={backgroundColor}>
            <Island>{children}</Island>
          </Content>
        </>
      )}
    </>
  );
};

export default PageTemplate;
